// Component: ReadinessPanel.jsx
// Purpose: R5.1-R5.7/R6.1 — itemized deployment-readiness checklist. Replaces the legacy Tally
//   Gate badge (StrategyBuilder.jsx) and the "Balanced" exit badge (TradeExecutionStep.jsx) with
//   ONE consolidated surface: one row per evaluator item (never a rolled-up pass/fail), reactive
//   (fed by StrategyBuilder's debounced POST .../readiness), inline reason+fix_hint (R6.1, no
//   modal), and a fix-affordance that jumps the canvas to the offending node(s) (R5.3).
//
//! Visual-gate fix (2026-07-31, docs/pipelines/2026-07-30-strategy-deploy-readiness-checklist):
//  the panel must NEVER render blank. Before this fix `!items.length && !loading` returned `null`
//  — on a brand-new/unsaved strategy the reactive eval never fires (no strategyId yet), so
//  `items` stayed `[]` forever and the ENTIRE panel (including its header) vanished — exactly the
//  state Deepak hit on Step 4 ("Where is the readiness checklist? I see nothing."). The panel now
//  always renders its header plus exactly one body state: unsaved CTA / evaluating / error-retry
//  / itemized (see `hasStrategyId`/`error` props below).
//
//! Collapsible-summary UX refinement (2026-07-31, Deepak's live-panel review — same pipeline
//  folder): three changes on top of the above, PRESENTATION ONLY (evaluator/item-data/fix
//  affordance untouched):
//   1. Dropped the per-row severity badge (`REQUIRED`/`RECOMMENDED` chip) — with items already
//      sub-grouped under "Required"/"Recommended" headers the per-row chip read as a broken
//      clickable control ("looks like I am missing something and I need to click and enter it").
//   2. The whole section is now collapsible behind a header (icon + heading + status summary +
//      chevron).
//   3. Status summary is ADDITIVE, never a replacement for the itemized list (R5.1.1 still holds
//      — expanding ALWAYS shows every item individually, never a merged aggregate). Default
//      expand state: collapsed when every REQUIRED item passes ("if everything is good I don't
//      even need to expand"), auto-expanded when any REQUIRED item is failing so the blocker is
//      immediately visible. The user can still toggle either way afterward.

import { useMemo, useState } from 'react';
import {
  FiCheckCircle, FiXCircle, FiMinusCircle, FiLoader, FiShield, FiLock, FiAlertCircle,
  FiAlertTriangle, FiChevronDown, FiChevronRight, FiHelpCircle,
} from 'react-icons/fi';

/*>>> State: no local state — pure render of the `items` prop the parent debounces/fetches. <<<*/

//! R5.1.2 — never render a raw node UUID. The server (readiness_evaluator.py resolve_node_label*)
//  already resolves `offending_node_labels` parallel to `offending_node_ids`; this is ONLY a
//  client-side fallback for the rare case that array is absent/short-mismatched — it joins
//  against the SAME `nodes` array StrategyBuilder.jsx already holds in canvas state. The regex
//  guards the fallback text itself so a bug here can never leak a bare UUID either.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function resolveOffendingLabels(item, nodesById) {
  const ids = item.offending_node_ids || [];
  const serverLabels = item.offending_node_labels;
  if (Array.isArray(serverLabels) && serverLabels.length === ids.length) {
    return ids.map((id, i) => ({ id, label: serverLabels[i] || 'unresolved node reference' }));
  }
  //--> Fallback join (should rarely trigger — see comment above).
  return ids.map((id) => {
    const node = nodesById[id];
    const label = node?.data?.label || node?.data?.name
      || (node?.data?.action ? `${node.type}: ${node.data.action}` : node?.type)
      || 'unresolved node reference';
    return { id, label: UUID_RE.test(String(label)) ? 'unresolved node reference' : label };
  });
}

//! #30 S4 warning-UX (Deepak, 2026-08-02) — `tone` (server-supplied: null | "caution" | "warning")
//  is checked FIRST, BEFORE the pass-based mark. A toned item is a passed=true item that must NOT
//  read as a plain green "all good" ✓: "warning" -> red alert, "caution" -> amber alert. Only when
//  tone is absent does the icon fall back to the existing pass-based ✓/✗/– rendering, so every
//  pre-existing (untoned) item is visually unchanged.
const MarkIcon = ({ pass, tone }) => {
  if (tone === 'warning') return <FiAlertCircle className="text-loss flex-shrink-0" size={15} />;
  if (tone === 'caution') return <FiAlertTriangle className="text-warning flex-shrink-0" size={15} />;
  if (pass === true) return <FiCheckCircle className="text-profit flex-shrink-0" size={15} />;
  if (pass === false) return <FiXCircle className="text-loss flex-shrink-0" size={15} />;
  //--> Neutral/indeterminate (e.g. circuit_bounds, R3.3.2) — NEVER a ✓ or ✗, a distinct dash mark.
  return <FiMinusCircle className="text-fg-3 flex-shrink-0" size={15} />;
};

const ReadinessItemRow = ({ item, nodesById, onSelectNode }) => {
  const offenders = useMemo(() => resolveOffendingLabels(item, nodesById), [item, nodesById]);
  const hasOffenders = offenders.length > 0;
  //* Per-row local UI state only (not fed by props/context) — "what does this check?" affordance,
  //  default COLLAPSED per Deepak ("by default we don't need to show it"). Independent per row
  //  (each ReadinessItemRow instance owns its own toggle) and independent of the fix-affordance
  //  button below — the `?` is about LEARNING, "Fix" is about ACTING, deliberately kept distinct.
  const [helpOpen, setHelpOpen] = useState(false);
  const hasExplanation = Boolean(item.explanation);

  /*>>> Handlers <<<*/
  const handleFixClick = () => {
    if (!hasOffenders) return;
    //--> R5.3 — jump to the FIRST offending node (setSelectedLogicNode is a single-node setter;
    //    the canvas highlight-chain effect (StrategyLogicStep.jsx traceUp/traceDown) already
    //    fans out from a single selected node, so this is sufficient for multi-offender items too).
    const node = nodesById[offenders[0].id];
    if (node) onSelectNode(node);
  };

  //! #30 S4 — row chrome follows `tone` FIRST (a passed=true toned item still gets a colored row),
  //  then falls back to the existing pass===false red row; untoned passing rows are unchanged.
  const rowToneClass = item.tone === 'warning'
    ? 'border-loss/25 bg-loss/5'
    : item.tone === 'caution'
      ? 'border-warning/30 bg-warning/5'
      : item.pass === false
        ? 'border-loss/25 bg-loss/5'
        : 'border-[var(--divider)] bg-bg-3';
  const labelToneClass = item.tone === 'warning'
    ? 'text-loss'
    : item.tone === 'caution'
      ? 'text-warning'
      : 'text-fg-1';

  return (
    <div className={`flex items-start gap-2.5 p-2.5 rounded-lg border ${rowToneClass}`}>
      <div className="mt-0.5"><MarkIcon pass={item.pass} tone={item.tone} /></div>
      <div className="min-w-0 flex-1">
        {/*//! Severity chip intentionally removed (2026-07-31 visual-gate fix) — the row already
            sits under the "Required"/"Recommended" group header, so a second per-row severity
            badge was redundant and read as a broken interactive control. <<<*/}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs font-bold ${labelToneClass}`}>{item.label}</span>
          {/*>>> Info affordance — "what does this check verify + why does it matter". Rendered
              ONLY when the evaluator supplied a static `explanation` (separate field from the
              dynamic `reason`); hidden entirely otherwise so items without one never show a
              dead-end toggle. Does not shift the ✓/✗ mark or the row's overall layout — it sits
              inline with the label, same row height. <<<*/}
          {hasExplanation && (
            <button
              type="button"
              onClick={() => setHelpOpen((prev) => !prev)}
              aria-expanded={helpOpen}
              aria-label="What does this check?"
              title="What does this check?"
              className="flex-shrink-0 text-fg-3 hover:text-fg-1 transition-colors"
            >
              <FiHelpCircle size={12} />
            </button>
          )}
        </div>
        {/*>>> R6.1 — reason + fix_hint rendered INLINE, immediately adjacent, no separate panel. <<<*/}
        {item.reason ? <p className="text-[11px] text-fg-3 mt-0.5">{item.reason}</p> : null}
        {item.fix_hint ? <p className="text-[11px] text-fg-2 mt-0.5 italic">{item.fix_hint}</p> : null}
        {/*>>> Expandable static explanation — collapsed by default, toggled by the `?` button
            above. A muted, indented, bordered box distinct from the dynamic reason/fix_hint text
            so it reads as reference material, not a live evaluator message. <<<*/}
        {hasExplanation && helpOpen && (
          <div className="mt-1.5 p-2 rounded-md border border-[var(--divider)] bg-bg-2">
            <p className="text-[11px] text-fg-3 leading-relaxed">{item.explanation}</p>
          </div>
        )}
        {hasOffenders && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {offenders.map((o) => (
              <span
                key={o.id}
                className="text-[10px] px-1.5 py-0.5 rounded bg-bg-2 border border-[var(--divider)] text-fg-2"
              >
                {o.label}
              </span>
            ))}
          </div>
        )}
      </div>
      {/*>>> R5.3 fix-affordance — explicit disabled state (not a silent no-op) when there is no
          offending node to jump to (e.g. R2.1 entry_present has none). <<<*/}
      <button
        type="button"
        onClick={handleFixClick}
        disabled={!hasOffenders}
        aria-disabled={!hasOffenders}
        title={hasOffenders ? 'Jump to node' : 'No specific node to jump to'}
        className={`text-[10px] font-semibold px-2 py-1 rounded-md flex-shrink-0 transition-colors ${
          hasOffenders
            ? 'text-accent hover:bg-accent/10 cursor-pointer'
            : 'text-fg-3 opacity-40 cursor-not-allowed'
        }`}
      >
        Fix
      </button>
    </div>
  );
};

//! `hasStrategyId` — is the strategy saved yet (StrategyBuilder.jsx passes `!!strategyId`). While
//  false the reactive readiness call never fires (StrategyBuilder.jsx:1535 gates on strategyId),
//  so `items` is permanently `[]` — this is NOT a "no items" state, it's a "nothing to evaluate
//  yet" state, and must render a distinct CTA rather than either blank or a false "all clear".
const ReadinessPanel = ({
  items = [],
  loading = false,
  nodes = [],
  onSelectNode,
  hasStrategyId = true,
  error = false,
}) => {
  const nodesById = useMemo(() => {
    const map = {};
    (nodes || []).forEach((n) => { if (n?.id) map[n.id] = n; });
    return map;
  }, [nodes]);

  const requiredItems = items.filter((i) => i.severity === 'REQUIRED');
  const recommendedItems = items.filter((i) => i.severity === 'RECOMMENDED');
  const hasItems = items.length > 0;

  const requiredFailingCount = requiredItems.filter((i) => i.pass === false).length;
  const recommendedFailingCount = recommendedItems.filter((i) => i.pass === false).length;
  const anyRequiredFailing = requiredFailingCount > 0;

  //! #30 S4 — a toned item (passed=true but flagged) must break the "All checks passed" summary so
  //  the attention state is visible even while the panel is collapsed. "warning" (red) outranks
  //  "caution" (amber); both are counted alongside the plain recommended-failing count.
  const warningToneCount = items.filter((i) => i.tone === 'warning').length;
  const cautionToneCount = items.filter((i) => i.tone === 'caution').length;

  //! Collapsible default — collapsed ONLY when there's an itemized result AND every REQUIRED item
  //  passes ("if everything is good, probably I don't even need to expand it"). Every other state
  //  (no items yet / any REQUIRED failing) starts expanded so the blocker or CTA is visible without
  //  an extra click. Computed once at mount via the lazy initializer; the toggle wins afterward —
  //  we deliberately do NOT force a re-collapse/re-expand on every reactive re-eval (per spec:
  //  "let the toggle win afterward").
  //! #30 S4 — also auto-expand when a red-tone warning is present (auto-close OFF on F&O): a
  //  money-path hazard should be visible without an extra click, same rationale as a failing REQUIRED.
  const [expanded, setExpanded] = useState(() => !hasItems || anyRequiredFailing || warningToneCount > 0);

  /*>>> Header status summary — additive only; NEVER replaces the itemized list below (R5.1.1
      still requires every item to render individually when expanded). <<<*/
  let summary;
  if (hasItems) {
    if (anyRequiredFailing) {
      summary = {
        text: `${requiredFailingCount} required need attention`,
        colorClass: 'text-loss',
        Icon: FiXCircle,
      };
    } else if (warningToneCount > 0) {
      //! #30 S4 — a red-tone warning (e.g. auto-close OFF on an F&O strategy) is a real money-path
      //  hazard: surface it in red even though the item is technically passed=true/RECOMMENDED.
      summary = {
        text: `${warningToneCount} warning${warningToneCount > 1 ? 's' : ''} need attention`,
        colorClass: 'text-loss',
        Icon: FiAlertCircle,
      };
    } else if (cautionToneCount > 0 || recommendedFailingCount > 0) {
      const attn = cautionToneCount + recommendedFailingCount;
      summary = {
        text: `${attn} to review`,
        colorClass: 'text-warning',
        Icon: FiAlertTriangle,
      };
    } else {
      summary = { text: 'All checks passed', colorClass: 'text-profit', Icon: FiCheckCircle };
    }
  } else if (!hasStrategyId) {
    summary = { text: 'Save to evaluate', colorClass: 'text-fg-3', Icon: FiLock };
  } else if (loading) {
    summary = { text: 'Evaluating…', colorClass: 'text-fg-3', Icon: FiLoader, spin: true };
  } else if (error) {
    summary = { text: 'Evaluation failed', colorClass: 'text-fg-3', Icon: FiAlertCircle };
  } else {
    summary = null;
  }

  /*>>> Body state — ALWAYS exactly one of: itemized / unsaved-CTA / evaluating / error-retry.
      The header below renders unconditionally so the panel can never be entirely blank. Only
      rendered when `expanded` — the header + summary alone are enough in the collapsed state. <<<*/
  let body;
  if (hasItems) {
    body = (
      <>
        {requiredItems.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold text-fg-3 uppercase tracking-wider">Required</p>
            {requiredItems.map((item) => (
              <ReadinessItemRow key={item.id} item={item} nodesById={nodesById} onSelectNode={onSelectNode} />
            ))}
          </div>
        )}

        {recommendedItems.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold text-fg-3 uppercase tracking-wider">Recommended</p>
            {recommendedItems.map((item) => (
              <ReadinessItemRow key={item.id} item={item} nodesById={nodesById} onSelectNode={onSelectNode} />
            ))}
          </div>
        )}

        {/*>>> H4 — the panel is a "readiness" surface, not a full "deployability" oracle: allocation/
            drift/lifecycle guards enforce independently and are not reflected as items here. <<<*/}
        <p className="text-[10px] text-fg-3">
          Deploy may still be blocked by account/allocation or lifecycle checks not shown here.
        </p>
      </>
    );
  } else if (!hasStrategyId) {
    //--> Unsaved state — the exact state Deepak hit. Never blank; explains WHY there's nothing yet.
    body = (
      <div className="flex items-start gap-2.5 p-2.5 rounded-lg border border-[var(--divider)] bg-bg-3">
        <FiLock className="text-fg-3 flex-shrink-0 mt-0.5" size={14} />
        <p className="text-xs text-fg-2 leading-relaxed">
          Save your strategy to see the deployment-readiness checklist.
        </p>
      </div>
    );
  } else if (loading) {
    //--> First evaluation, no prior items to show yet.
    body = (
      <div className="flex items-center gap-2 p-2.5 rounded-lg border border-[var(--divider)] bg-bg-3">
        <FiLoader className="animate-spin text-fg-3 flex-shrink-0" size={14} />
        <p className="text-xs text-fg-2">Evaluating readiness…</p>
      </div>
    );
  } else if (error) {
    //--> Reactive call failed — non-alarming, the debounce re-fires on the next edit (:1534-1552).
    body = (
      <div className="flex items-start gap-2.5 p-2.5 rounded-lg border border-[var(--divider)] bg-bg-3">
        <FiAlertCircle className="text-fg-3 flex-shrink-0 mt-0.5" size={14} />
        <p className="text-xs text-fg-2 leading-relaxed">
          Couldn&apos;t evaluate readiness — it will retry as you edit.
        </p>
      </div>
    );
  } else {
    body = null;
  }

  const SummaryIcon = summary?.Icon;

  return (
    <div className="space-y-2">
      {/*>>> Header — ALWAYS visible; toggles `expanded`. Deepak's requirement #2/#3: a proper,
          collapsible section heading with a status summary so a fully-passing panel never needs
          to be expanded to be trusted. Mirrors DeploymentSection.jsx's `CollapsibleCard`
          (chevron + icon + title + trailing summary convention, DeploymentSection.jsx:291-309) —
          not reinvented as a new pattern; the one difference from that analogue is the summary
          stays visible in BOTH states here (not collapsed-only), since Deepak's ask is an
          always-on-top status, not just a collapsed-state hint. <<<*/}
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        className="w-full flex items-center gap-2 text-left group"
      >
        {expanded
          ? <FiChevronDown size={12} className="text-fg-3 flex-shrink-0" />
          : <FiChevronRight size={12} className="text-fg-3 flex-shrink-0" />}
        <FiShield size={12} className="text-fg-3 flex-shrink-0" />
        <h4 className="text-xs font-bold text-fg-3 uppercase tracking-widest group-hover:text-fg-2">Deployment readiness</h4>
        {loading && hasItems ? <FiLoader className="animate-spin text-fg-3" size={12} /> : null}
        {summary ? (
          <span className={`ml-auto flex items-center gap-1 text-[11px] font-semibold whitespace-nowrap ${summary.colorClass}`}>
            {SummaryIcon ? <SummaryIcon size={12} className={summary.spin ? 'animate-spin' : ''} /> : null}
            {summary.text}
          </span>
        ) : null}
      </button>
      {expanded ? <div className="space-y-3">{body}</div> : null}
    </div>
  );
};

export default ReadinessPanel;
